import { AfterViewChecked, Component, ElementRef, HostListener, OnDestroy, OnInit, ViewChild } from '@angular/core'
import { ApiService } from 'src/app/services/api.service'
import { UiService } from 'src/app/services/ui.service'
import { Router } from '@angular/router'
import { FormBuilder, UntypedFormGroup } from '@angular/forms'
import { AccountDto, ApplicationType, SyncPairDto, SyncPhaseDto } from 'src/app/services/dtos'
import { SyncPair } from 'src/app/account/services/sync-pair.service'
import { ViewableIssue } from 'src/app/account/components/issues-view/issues-view.component'
import {
  StatusContext,
  ViewableApplicationResource,
} from 'src/app/account/components/app-resources-view/app-resources-view.component'
import { ResourcesTab } from 'src/app/account/components/application/resources-tab'
import {
  BackupHealthStatus,
  computeRecoveryEnvStatus,
  FailbackStatus,
  RecoveryEnvironmentStatus,
  STATUS_TEXT,
} from 'src/app/account/components/application/statusText'
import { AppStatusState, ProcessStatus } from 'src/app/account/components/process-state/process-state.component'
import { forkJoin, Observable, of, Subject, throwError } from 'rxjs'
import { catchError, map, takeUntil, timeout } from 'rxjs/operators'
import {
  BackupScreenScenario,
  buildAllMockIssues,
  FAILBACK_NO_RESOURCES_TOOLTIP,
  buildBackupScreenScenarios,
  buildDuplicateRowsScenario,
  buildTripledNameScenario,
  buildMockAccessTemplates,
  buildMockApplication,
  buildMockAzureQuarantinedResources,
  buildMockChartData,
  buildMockFirewallEvents,
  buildMockJobDto,
  buildMockQuarantinedResources,
  buildMockSyncPairAccess,
  buildRecoveryScreenScenarios,
  buildParentChildScenarioResources,
  buildStatusResources,
  CHART_SCENARIOS,
  ChartScenarioKey,
  getMockChartRpo,
  LAUNCH_TEST_SANDBOX_SCENARIOS,
  SANDBOX_CONFIG_SCENARIOS,
  SandboxScenario,
  RECOVERY_SCREEN_TABS,
  RecoveryScreenScenario,
  STACKSETS_JOB_IDS,
  STACKSETS_JOB_SCENARIOS,
  STACKSETS_SCENARIO_PRESETS,
  StacksetsJobScenarioKey,
  StacksetsScenarioPreset,
} from './test-page-mock-data'
import { JobDto, JobStatus, RecoveryPointAttemptDto } from 'src/app/services/dtos'
import { HttpError } from 'src/utils/errors'
import { AccessState } from 'src/app/services/access.service'
import { Application, ApplicationProviderService } from 'src/app/account/services/application.service'
import { AccountService } from 'src/app/account/services/account.service'
import { AccessLevelService } from 'src/app/services/access-level.service'
import { StacksetsRegenerationPollService } from 'src/app/shared/services/stacksets-regeneration-poll.service'
import { ModalDialogComponent } from 'src/app/shared/modal-dialog/modal-dialog.component'
import { NewRoleModalComponent } from 'src/app/account/components/new-role-modal/new-role-modal.component'
import { EditRoleModalComponent } from 'src/app/account/components/edit-role-modal/edit-role-modal.component'
import { NewGroupModalComponent } from 'src/app/account/components/new-group-modal/new-group-modal.component'
import { EditGroupModalComponent } from 'src/app/account/components/edit-group-modal/edit-group-modal.component'
import { EditUserModalComponent } from 'src/app/account/components/edit-user-modal/edit-user-modal.component'
import { InviteUsersModalComponent } from 'src/app/account/components/invite-users-modal/invite-users-modal.component'
import { NewApiKeyModalComponent } from 'src/app/account/components/new-api-key-modal/new-api-key-modal.component'
import { EditApiKeyModalComponent } from 'src/app/account/components/edit-api-key-modal/edit-api-key-modal.component'
import { IdpModalComponent } from 'src/app/account/components/shared/idp-modal/idp-modal.component'

const fakeSyncPair = {
  sourceAwsAccountId: '111111',
  sourceRegion: 'aws-source-1',
  targetAwsAccountId: '222222',
  targetRegion: 'aws-target-1',
  sourceEndpointUri: 'fake-source-endpoint',
  targetEndpointUri: 'fake-target-endpoint',
} as SyncPairDto

@Component({
  selector: 'app-test-page',
  templateUrl: './test-page.component.html',
  styleUrls: ['./test-page.component.scss'],
  standalone: false,
})
export class TestPageComponent implements OnInit, OnDestroy, AfterViewChecked {
  // 'loading' → spinner; 'allowed' → showcase; 'denied' → standard 404.
  // Gated on `principal.adminRole != null` — any non-null AdminRoleDto
  // (readOnly / accountManager / full) qualifies as a "legacy admin",
  // matching how access-level.service derives `isLegacyAdmin`.
  gateState: 'loading' | 'allowed' | 'denied' = 'loading'

  accounts: Array<AccountDto> = null
  syncPair: SyncPair = null
  application: Application = null
  issues: ViewableIssue[] = []
  duplicateRowsIssues: ViewableIssue[] = []
  tripledNameIssues: ViewableIssue[] = []
  statusResources: ViewableApplicationResource[] = []
  parentChildResources: ViewableApplicationResource[] = []
  RecoveryTab = ResourcesTab.RECOVERY_ENVIRONMENT
  BackupTab = ResourcesTab.BACKUP
  StatusContext = StatusContext

  readonly ACCOUNTS_PREVIEW = 6
  showAllAccounts = false
  // Issues / resources are collapsed by default. They reference
  // mock ARNs that aren't real on the backend, which fires a barrage of 404s
  // and (for some issue components) NPEs from their ngOnInit. Expand manually
  // when actually testing those components.
  expandedSections: { [key: string]: boolean } = {
    accounts: false,
    issues: false,
    issuesScenarios: false,
    resources: false,
    recoveryScreen: false,
    backupScreen: false,
    tabStatus: false,
    tabStatusBackup: false,
    tabStatusRecoveryEnv: false,
    tabStatusFailback: false,
    modals: false,
    chart: false,
    stacksets: false,
  }

  // Tab-status-bar showcase — grouped by tab then sync phase so reviewers can
  // see every state computeBackupHealthStatus / computeRecoveryEnvStatus /
  // computeFailbackStatus can produce. Mock objects only; production code is
  // never modified (per CLAUDE-TEST-PAGE.md). Copy comes from STATUS_TEXT so
  // wording stays in sync.
  readonly tabStatusGroups: ReadonlyArray<{
    key: string
    tab: string
    phases: ReadonlyArray<{
      phase: string
      variants: ReadonlyArray<{
        label: string
        status: BackupHealthStatus | RecoveryEnvironmentStatus | FailbackStatus
      }>
    }>
  }> = [
    {
      key: 'tabStatusBackup',
      tab: 'Backup Health',
      phases: [
        {
          phase: 'Active backup (any sync phase)',
          variants: [
            {
              label: 'Creating initial backup — no prior success',
              status: {
                state: AppStatusState.INDETERMINATE,
                text: STATUS_TEXT.backup.creatingInitial,
                progressCount: 3,
                progressTotal: 12,
                issues: [],
              },
            },
            {
              label: 'Creating backup — with prior success',
              status: {
                state: AppStatusState.INDETERMINATE,
                text: STATUS_TEXT.backup.creatingNew,
                progressCount: 7,
                progressTotal: 12,
                priorBackupInfo: STATUS_TEXT.backup.priorBackupInfo(
                  '2 hours ago',
                  ' from recovery point at 01:14 PM on May 8',
                  12
                ),
                issues: [],
              },
            },
          ],
        },
        {
          phase: 'Standby',
          variants: [
            {
              label: 'Planning backup — queued resources',
              status: {
                state: AppStatusState.INDETERMINATE,
                text: STATUS_TEXT.backup.planningBackup,
                priorBackupInfo: STATUS_TEXT.backup.lastSuccessful(
                  '4 hours ago',
                  ' from recovery point at 11:14 AM on May 8'
                ),
                issues: [],
              },
            },
            {
              label: 'Last successful backup — clean',
              status: {
                state: AppStatusState.SUCCESS,
                text: STATUS_TEXT.backup.lastSuccessful('2 hours ago', ' from recovery point at 01:14 PM on May 8.'),
                issues: [],
              },
            },
            {
              label: 'Last successful backup — with open issues',
              status: {
                state: AppStatusState.WARNING,
                text: STATUS_TEXT.backup.lastSuccessful('2 hours ago', ' from recovery point at 01:14 PM on May 8.'),
                issues: [{} as any],
              },
            },
            {
              label: 'Last successful backup — partial (some resources didn’t process)',
              status: {
                state: AppStatusState.WARNING,
                text: STATUS_TEXT.backup.lastSuccessful('2 hours ago', ' from recovery point at 01:14 PM on May 8.'),
                issues: [],
              },
            },
            {
              label: 'Failed to back up N resources',
              status: {
                state: AppStatusState.ERROR,
                text: STATUS_TEXT.backup.failedToBackupCount(3, ' at 10:25 AM on May 9'),
                failedLink: { ...STATUS_TEXT.backup.failedLinkBackup, count: 3, suffix: ' at 10:25 AM on May 9.' },
                priorBackupInfo: STATUS_TEXT.backup.lastSuccessful(
                  '1 day ago',
                  ' from recovery point at 10:14 AM on May 8'
                ),
                issues: [],
              },
            },
            {
              label: 'Failed to create backup — no resource-level failures',
              status: {
                state: AppStatusState.ERROR,
                text: STATUS_TEXT.backup.failedToCapture(' at 10:25 AM on May 9'),
                priorBackupInfo: STATUS_TEXT.backup.lastSuccessful(
                  '1 day ago',
                  ' from recovery point at 10:14 AM on May 8'
                ),
                issues: [],
              },
            },
          ],
        },
        {
          phase: 'Frozen (during recovery / test)',
          variants: [
            {
              label: 'Last successful backup — frozen during recovery',
              status: {
                state: AppStatusState.SUCCESS,
                text: STATUS_TEXT.backup.lastSuccessful('3 hours ago', ' from recovery point at 12:14 PM on May 8.'),
                issues: [],
              },
            },
            {
              label: 'No backup data available — no recovery point yet',
              status: { state: AppStatusState.INDETERMINATE, text: STATUS_TEXT.backup.noDataAvailable, issues: [] },
            },
          ],
        },
      ],
    },
    {
      key: 'tabStatusRecoveryEnv',
      tab: 'Recovery Environment',
      phases: [
        {
          phase: 'Standby',
          variants: [
            {
              label: 'Initial backup in progress',
              status: {
                state: AppStatusState.INDETERMINATE,
                text: STATUS_TEXT.recovery.firstBackupInProgress,
                issues: [],
              },
            },
            {
              label: 'Waiting for first backup',
              status: {
                state: AppStatusState.INDETERMINATE,
                text: STATUS_TEXT.recovery.waitingForFirstBackup,
                issues: [],
              },
            },
            {
              label: 'Waiting to perform initial restore',
              status: {
                state: AppStatusState.INDETERMINATE,
                text: STATUS_TEXT.recovery.waitingForInitialRestore,
                issues: [],
              },
            },
            {
              label: 'Standby restore blocked by sibling failover',
              status: {
                state: AppStatusState.INFO,
                text: STATUS_TEXT.recovery.standbyRestoreBlocked('us-west-1', 'us-west-2'),
                issues: [],
              },
            },
            {
              label: 'Performing initial restore',
              status: {
                state: AppStatusState.INDETERMINATE,
                text: STATUS_TEXT.recovery.performingInitialRestore,
                issues: [],
              },
            },
            {
              label: 'Restoring backup with progress',
              status: {
                state: AppStatusState.INDETERMINATE,
                text: STATUS_TEXT.recovery.restoringBackup('5 minutes ago'),
                ratioText: '40% restored (4/10) from recovery point at 03:14 PM on May 8.',
                progressCount: 4,
                progressTotal: 10,
                issues: [],
              },
            },
            // Run through the real computeRecoveryEnvStatus so these exercise the resume warming-up
            // gate (no bar/ratio until a resource completes), not a hand-authored look-alike.
            {
              label: 'Resume — warming up (0 of 8 restored, bar suppressed)',
              status: computeRecoveryEnvStatus({
                syncPhase: SyncPhaseDto.FAILOVER,
                syncStatus: ProcessStatus.SUBSEQUENT_ATTEMPT_IN_PROGRESS,
                appliedRpTimestamp: new Date(),
                failedRestoreCount: 0,
                restoreIssues: [],
                appIsDeleted: false,
                allResourcesCleaned: false,
                successCount: 0,
                totalCount: 8,
                inProgressCount: 8,
              }),
            },
            {
              label: 'Resume — first resource done (1 of 8, bar returns)',
              status: computeRecoveryEnvStatus({
                syncPhase: SyncPhaseDto.FAILOVER,
                syncStatus: ProcessStatus.SUBSEQUENT_ATTEMPT_IN_PROGRESS,
                appliedRpTimestamp: new Date(),
                failedRestoreCount: 0,
                restoreIssues: [],
                appIsDeleted: false,
                allResourcesCleaned: false,
                successCount: 1,
                totalCount: 8,
                inProgressCount: 7,
              }),
            },
            {
              // A fresh failure (not just a success) also ends the warming-up window, so the bar
              // returns at an accurate 0% rather than staying an indeterminate spinner.
              label: 'Resume — first resource failed (1 of 8 failed, bar returns at 0%)',
              status: computeRecoveryEnvStatus({
                syncPhase: SyncPhaseDto.FAILOVER,
                syncStatus: ProcessStatus.SUBSEQUENT_ATTEMPT_IN_PROGRESS,
                appliedRpTimestamp: new Date(),
                failedRestoreCount: 1,
                restoreIssues: [],
                appIsDeleted: false,
                allResourcesCleaned: false,
                successCount: 0,
                totalCount: 8,
                inProgressCount: 7,
              }),
            },
            {
              label: 'Successfully restored',
              status: {
                state: AppStatusState.SUCCESS,
                text: STATUS_TEXT.recovery.successfullyRestored('1 hour ago'),
                ratioText: '100% restored (8/8) from recovery point at 03:14 PM on May 8.',
                issues: [],
              },
            },
            {
              label: 'Successfully restored with open issues',
              status: {
                state: AppStatusState.WARNING,
                text: STATUS_TEXT.recovery.successfullyRestored('1 hour ago'),
                ratioText: '100% restored (8/8) from recovery point at 03:14 PM on May 8.',
                issues: [{} as any, {} as any],
              },
            },
            {
              label: 'Failed to restore N resources',
              status: {
                state: AppStatusState.ERROR,
                text: STATUS_TEXT.recovery.failedToRestoreCount(2, '1 hour ago'),
                ratioText: '75% restored (6/8) from recovery point at 03:14 PM on May 8.',
                failedLink: { ...STATUS_TEXT.recovery.failedLinkRestore('1 hour ago'), count: 2 },
                issues: [],
              },
            },
            {
              label: 'Initial restore failed',
              status: { state: AppStatusState.ERROR, text: STATUS_TEXT.recovery.initialRestoreFailed, issues: [] },
            },
            {
              label: 'Failed to apply backup',
              status: {
                state: AppStatusState.ERROR,
                text: STATUS_TEXT.recovery.failedToApplyBackup('30 minutes ago'),
                issues: [],
              },
            },
          ],
        },
        {
          phase: 'Failover',
          variants: [
            {
              label: 'Preparing to restore for recovery',
              status: {
                state: AppStatusState.INDETERMINATE,
                text: STATUS_TEXT.recovery.preparingRestore(false),
                issues: [],
              },
            },
            {
              label: 'Restoring for recovery with progress',
              status: {
                state: AppStatusState.INDETERMINATE,
                text: STATUS_TEXT.recovery.restoreInProgress(false),
                ratioText: '60% restored (6/10) from recovery point at 03:14 PM on May 8.',
                progressCount: 6,
                progressTotal: 10,
                issues: [],
              },
            },
            {
              label: 'All resources restored for recovery',
              status: {
                state: AppStatusState.SUCCESS,
                text: STATUS_TEXT.recovery.allResourcesRestored('15 minutes ago', false),
                ratioText: '100% restored (10/10) from recovery point at 03:14 PM on May 8.',
                issues: [],
              },
            },
            {
              label: 'Failed to restore N resources for recovery',
              status: {
                state: AppStatusState.ERROR,
                text: STATUS_TEXT.recovery.failedToRestoreCount(3, '15 minutes ago'),
                ratioText: '70% restored (7/10) from recovery point at 03:14 PM on May 8.',
                failedLink: { ...STATUS_TEXT.recovery.failedLinkRestore('15 minutes ago'), count: 3 },
                issues: [],
              },
            },
            {
              label: 'Restore for recovery failed — terminal',
              status: { state: AppStatusState.ERROR, text: STATUS_TEXT.recovery.restoreFailed(false), issues: [] },
            },
          ],
        },
        {
          phase: 'Failover Test',
          variants: [
            {
              label: 'Preparing to restore for test recovery',
              status: {
                state: AppStatusState.INDETERMINATE,
                text: STATUS_TEXT.recovery.preparingRestore(true),
                issues: [],
              },
            },
            {
              label: 'Restoring for test recovery with progress',
              status: {
                state: AppStatusState.INDETERMINATE,
                text: STATUS_TEXT.recovery.restoreInProgress(true),
                ratioText: '60% restored (6/10) from recovery point at 03:14 PM on May 8.',
                progressCount: 6,
                progressTotal: 10,
                issues: [],
              },
            },
            {
              label: 'All resources restored for test recovery',
              status: {
                state: AppStatusState.SUCCESS,
                text: STATUS_TEXT.recovery.allResourcesRestored('15 minutes ago', true),
                ratioText: '100% restored (10/10) from recovery point at 03:14 PM on May 8.',
                issues: [],
              },
            },
            {
              label: 'Failed to restore N resources for test recovery',
              status: {
                state: AppStatusState.ERROR,
                text: STATUS_TEXT.recovery.failedToRestoreCount(3, '15 minutes ago'),
                ratioText: '70% restored (7/10) from recovery point at 03:14 PM on May 8.',
                failedLink: { ...STATUS_TEXT.recovery.failedLinkRestore('15 minutes ago'), count: 3 },
                issues: [],
              },
            },
            {
              label: 'Restore for test recovery failed — terminal',
              status: { state: AppStatusState.ERROR, text: STATUS_TEXT.recovery.restoreFailed(true), issues: [] },
            },
          ],
        },
        {
          phase: 'Deleted app (cleanup)',
          variants: [
            {
              label: 'Cleaning up recovery environment resources',
              status: { state: AppStatusState.INDETERMINATE, text: STATUS_TEXT.recovery.cleaningUp, issues: [] },
            },
            {
              label: 'Finalizing application deletion',
              status: {
                state: AppStatusState.INDETERMINATE,
                text: STATUS_TEXT.recovery.finalizingDeletion,
                issues: [],
              },
            },
            {
              label: 'Recovery environment cleanup failed',
              status: {
                state: AppStatusState.ERROR,
                text: STATUS_TEXT.recovery.cleanupFailed,
                issues: [{} as any],
              },
            },
          ],
        },
      ],
    },
    {
      key: 'tabStatusFailback',
      tab: 'Failback',
      phases: [
        {
          phase: 'Any sync phase',
          variants: [
            {
              label: 'No resources support failback',
              status: { state: AppStatusState.INFO, text: STATUS_TEXT.failback.noSupported, issues: [] },
            },
            {
              label: 'Resources available for failback',
              status: { state: AppStatusState.SUCCESS, text: STATUS_TEXT.failback.available(8), issues: [] },
            },
            {
              label: 'Returning resources with progress',
              status: {
                state: AppStatusState.INDETERMINATE,
                text: STATUS_TEXT.failback.returning,
                ratioText: '50% returned (4/8).',
                progressCount: 4,
                progressTotal: 8,
                issues: [],
              },
            },
            {
              label: 'Retrying return to primary',
              status: {
                state: AppStatusState.INDETERMINATE,
                text: STATUS_TEXT.failback.retrying,
                ratioText: '75% returned (6/8).',
                progressCount: 6,
                progressTotal: 8,
                issues: [],
              },
            },
            {
              label: 'All resources returned',
              status: {
                state: AppStatusState.SUCCESS,
                text: STATUS_TEXT.failback.allReturned,
                ratioText: '100% returned (8/8).',
                issues: [],
              },
            },
            {
              label: 'Failed to return N resources',
              status: {
                state: AppStatusState.ERROR,
                text: STATUS_TEXT.failback.failedToReturnCount(2),
                ratioText: '75% returned (6/8).',
                failedLink: { ...STATUS_TEXT.failback.failedLinkReturn, count: 2 },
                issues: [],
              },
            },
            {
              label: 'Failback failed — terminal',
              status: { state: AppStatusState.ERROR, text: STATUS_TEXT.failback.failed, issues: [] },
            },
          ],
        },
      ],
    },
  ]

  // Total variant count (used in the section header chip).
  get tabStatusVariantCount(): number {
    return this.tabStatusGroups.reduce((sum, group) => sum + group.phases.reduce((s, p) => s + p.variants.length, 0), 0)
  }

  // Recovery / Failback screen-preview scenarios (ARP-7143). Each scenario
  // carries a fully-realized snapshot — application, resources, status, and
  // operation-notice fields — so the section can render the composed screen
  // without orchestrating real resources or recoveries.
  readonly recoveryScreenScenarios: ReadonlyArray<RecoveryScreenScenario> = buildRecoveryScreenScenarios()
  recoveryScreenScenarioKey: string = this.recoveryScreenScenarios[0].key
  readonly RECOVERY_SCREEN_TABS = RECOVERY_SCREEN_TABS
  // Tooltip for a disabled FAILBACK button when no failback resources exist
  // (shown only when there's no higher-priority disabledActionReason).
  readonly failbackNoResourcesTooltip = FAILBACK_NO_RESOURCES_TOOLTIP

  get recoveryScreenActiveScenario(): RecoveryScreenScenario {
    return (
      this.recoveryScreenScenarios.find((s) => s.key === this.recoveryScreenScenarioKey) ??
      this.recoveryScreenScenarios[0]
    )
  }

  // Convenience getters bound directly in the template so the screen markup
  // mirrors what application.component.html does with its component getters.
  get screenApp(): Application {
    return this.recoveryScreenActiveScenario.application
  }
  get screenResources() {
    return this.recoveryScreenActiveScenario.resources
  }
  get screenStatus() {
    return this.recoveryScreenActiveScenario.status
  }
  get screenNotice() {
    return this.recoveryScreenActiveScenario.operationNotice
  }
  get screenFailbackNoticeText(): string | null {
    return this.recoveryScreenActiveScenario.failbackOperationNoticeText
  }
  get screenIsFailbackTab(): boolean {
    return this.recoveryScreenActiveScenario.tab === 'failback'
  }
  // Mirrors application.component's failback visibility: the Recovery/Failback
  // sub-tab chips only appear in FAILOVER phase, entitled (assumed here), and
  // when resources aren't quarantined. STANDBY / FAILOVER_TEST / CLEANUP and the
  // quarantined recovery scenario render the Recovery content with no sub-tabs.
  get screenShowsFailbackSubTabs(): boolean {
    const scenario = this.recoveryScreenActiveScenario
    return scenario.syncPhase === SyncPhaseDto.FAILOVER && !scenario.tabBarFlags.showUnquarantine
  }
  get screenTabType() {
    return this.screenIsFailbackTab ? RECOVERY_SCREEN_TABS.failback : RECOVERY_SCREEN_TABS.recovery
  }

  // Grouped scenarios for the picker <optgroup>s — preserves the source order
  // so STANDBY > FAILOVER > FAILOVER_TEST > CLEANUP > FAILBACK reads top-to-bottom.
  get recoveryScreenScenarioGroups(): { group: string; label: string; scenarios: RecoveryScreenScenario[] }[] {
    const labelFor: Record<RecoveryScreenScenario['group'], string> = {
      STANDBY: 'Recovery — Standby',
      FAILOVER: 'Recovery — Failover',
      FAILOVER_TEST: 'Recovery — Failover Test',
      CLEANUP: 'Recovery — Cleanup',
      FAILBACK: 'Failback',
    }
    const groups: { group: string; label: string; scenarios: RecoveryScreenScenario[] }[] = []
    for (const scenario of this.recoveryScreenScenarios) {
      const existing = groups.find((g) => g.group === scenario.group)
      if (existing) existing.scenarios.push(scenario)
      else groups.push({ group: scenario.group, label: labelFor[scenario.group], scenarios: [scenario] })
    }
    return groups
  }

  setRecoveryScreenScenario(key: string): void {
    if (this.recoveryScreenScenarioKey === key) return
    this.recoveryScreenScenarioKey = key
  }

  // The chip buttons in the preview render with the active scenario's tab
  // highlighted, but their click handler is intentionally inert — clicking
  // inside the screen preview must not change which scenario is selected.
  // The display-wide capture-phase click blocker (see _attachScreenPreviewBlocker)
  // catches the click before it reaches this method; keeping the handler here
  // is harmless and preserves the production button signature.
  switchRecoveryScreenTab(_targetTab: 'recovery' | 'failback'): void {
    // No-op: the scenario picker dropdown is the only way to change scenarios.
  }

  // Backup screen-preview scenarios — Backup tab counterpart to Recovery.
  // Independent picker; same tab-bar action gating; same click-blocker
  // pattern applied to its own display element.
  readonly backupScreenScenarios: ReadonlyArray<BackupScreenScenario> = buildBackupScreenScenarios()
  backupScreenScenarioKey: string = this.backupScreenScenarios[0].key

  get backupScreenActiveScenario(): BackupScreenScenario {
    return (
      this.backupScreenScenarios.find((s) => s.key === this.backupScreenScenarioKey) ?? this.backupScreenScenarios[0]
    )
  }
  get backupScreenApp(): Application {
    return this.backupScreenActiveScenario.application
  }
  get backupScreenResources() {
    return this.backupScreenActiveScenario.resources
  }
  get backupScreenStatus() {
    return this.backupScreenActiveScenario.status
  }

  get backupScreenScenarioGroups(): { group: string; label: string; scenarios: BackupScreenScenario[] }[] {
    const labelFor: Record<BackupScreenScenario['group'], string> = {
      STANDBY: 'Standby',
      FAILOVER: 'Failover (frozen)',
    }
    const groups: { group: string; label: string; scenarios: BackupScreenScenario[] }[] = []
    for (const scenario of this.backupScreenScenarios) {
      const existing = groups.find((g) => g.group === scenario.group)
      if (existing) existing.scenarios.push(scenario)
      else groups.push({ group: scenario.group, label: labelFor[scenario.group], scenarios: [scenario] })
    }
    return groups
  }

  setBackupScreenScenario(key: string): void {
    if (this.backupScreenScenarioKey === key) return
    this.backupScreenScenarioKey = key
  }

  // ── Screen-preview click blocker ─────────────────────────────────
  // The Recovery / Failback screen preview is read-only. Hover effects and
  // PrimeNG tooltips must keep working (so reviewers can inspect tooltips /
  // disabled-button states), so we can't use `pointer-events: none` on the
  // wrapper — that kills hover too. Instead we attach a capture-phase click
  // (+ keyboard activation) blocker on the frame element. Capture phase runs
  // before any child handler, so chip clicks, app-resources-view row clicks,
  // filter pills, expand chevrons, etc. all get short-circuited.
  @ViewChild('recoveryScreenDisplay') recoveryScreenDisplay?: ElementRef<HTMLElement>
  @ViewChild('backupScreenDisplay') backupScreenDisplay?: ElementRef<HTMLElement>
  // Track which elements the listeners are attached to — when an accordion
  // collapses + re-expands, *ngIf destroys & recreates the wrapper, so the
  // identity check re-attaches to the new element. One slot per section so
  // both displays stay independently inert.
  private _screenPreviewBlockedEls = new Map<'recovery' | 'backup', HTMLElement | null>([
    ['recovery', null],
    ['backup', null],
  ])

  private _blockScreenPreviewClick = (event: Event): void => {
    event.preventDefault()
    event.stopPropagation()
    event.stopImmediatePropagation()
  }

  private _blockScreenPreviewKey = (event: KeyboardEvent): void => {
    // Block Enter / Space activations on focusable elements (buttons, links).
    if (event.key === 'Enter' || event.key === ' ' || event.key === 'Spacebar') {
      event.preventDefault()
      event.stopPropagation()
      event.stopImmediatePropagation()
    }
  }

  private _syncBlockerOnElement(key: 'recovery' | 'backup', el: HTMLElement | null): void {
    const prev = this._screenPreviewBlockedEls.get(key) ?? null
    if (el === prev) return
    if (prev) {
      prev.removeEventListener('click', this._blockScreenPreviewClick, true)
      prev.removeEventListener('mousedown', this._blockScreenPreviewClick, true)
      prev.removeEventListener('keydown', this._blockScreenPreviewKey, true)
    }
    if (el) {
      el.addEventListener('click', this._blockScreenPreviewClick, true)
      // mousedown blocks focus shifts that would otherwise highlight an inert button.
      el.addEventListener('mousedown', this._blockScreenPreviewClick, true)
      el.addEventListener('keydown', this._blockScreenPreviewKey, true)
    }
    this._screenPreviewBlockedEls.set(key, el)
  }

  ngAfterViewChecked(): void {
    this._syncBlockerOnElement('recovery', this.recoveryScreenDisplay?.nativeElement ?? null)
    this._syncBlockerOnElement('backup', this.backupScreenDisplay?.nativeElement ?? null)
  }

  // RPO chart scenario testing — pick a state to drive the rpo-chart input.
  readonly chartScenarios = CHART_SCENARIOS
  chartScenario: ChartScenarioKey = 'healthy'
  chartData: RecoveryPointAttemptDto[] | null = buildMockChartData('healthy')
  // RPO target (seconds) — most scenarios use 5 min; real-time uses 0 to
  // flip the chart into replication-latency rendering.
  chartRpoSeconds: number = getMockChartRpo('healthy')

  setChartScenario(key: ChartScenarioKey): void {
    if (this.chartScenario === key) return
    this.chartScenario = key
    this.chartData = buildMockChartData(key)
    this.chartRpoSeconds = getMockChartRpo(key)
  }

  // StackSets regeneration-job scenario testing — drives the patched getJob's
  // status sequence so reviewers can step through every gating outcome.
  readonly stacksetsJobScenarios = STACKSETS_JOB_SCENARIOS
  stacksetsJobScenario: StacksetsJobScenarioKey = 'none'
  // Anchored on the user's tab click so timed transitions ('running_then_done', etc.)
  // restart from zero each time — not from component init.
  private stacksetsScenarioStartMs = 0

  get stacksetsActiveJobId(): string | null {
    return STACKSETS_JOB_IDS[this.stacksetsJobScenario]
  }

  setStacksetsJobScenario(key: StacksetsJobScenarioKey): void {
    if (this.stacksetsJobScenario === key) return
    this.stacksetsJobScenario = key
    this.stacksetsScenarioStartMs = Date.now()
    // Distinct jobId per scenario triggers ngOnChanges in the showcased components,
    // which restarts polling — no unmount/remount needed (would cause page-scroll jump).

    // Shorten the regen poll timeout to 1s for the simulated-timeout scenario so reviewers
    // can see the timeout toast fire without waiting the production 120s.
    if (key === 'running_timeout') {
      this.regenerationPollService.setTimeoutMsForTesting(1000)
    } else {
      this.regenerationPollService.resetTimeoutMsForTesting()
    }
  }

  setStacksetsJobScenarioByKey(key: string): void {
    const scenario = this.stacksetsJobScenarios.find((s) => s.key === key)
    if (scenario) this.setStacksetsJobScenario(scenario.key)
  }

  get stacksetsJobScenarioIndex(): number {
    return this.stacksetsJobScenarios.findIndex((s) => s.key === this.stacksetsJobScenario)
  }

  // Scenario preset — a curated set of @Input combinations covering every distinct
  // UI variant the two showcased components render (create vs migration flow,
  // CFN-conflict warnings, managed/onboarding states, test-access progress/retry,
  // setup vs update mode, etc.). The regen-job axis above is independent.
  readonly stacksetsScenarioPresets = STACKSETS_SCENARIO_PRESETS
  stacksetsScenarioKey: string = STACKSETS_SCENARIO_PRESETS[0].key

  get stacksetsActivePreset(): StacksetsScenarioPreset {
    return (
      this.stacksetsScenarioPresets.find((p) => p.key === this.stacksetsScenarioKey) ?? this.stacksetsScenarioPresets[0]
    )
  }

  get stacksetsScenarioGroups(): { group: string; presets: StacksetsScenarioPreset[] }[] {
    const groups: { group: string; presets: StacksetsScenarioPreset[] }[] = []
    for (const preset of this.stacksetsScenarioPresets) {
      const existing = groups.find((g) => g.group === preset.group)
      if (existing) existing.presets.push(preset)
      else groups.push({ group: preset.group, presets: [preset] })
    }
    return groups
  }

  setStacksetsScenarioPreset(key: string): void {
    if (this.stacksetsScenarioKey === key) return
    this.stacksetsScenarioKey = key
    // Reset timed-transition anchor so running_then_done etc. start counting
    // from the picker change, not from page load.
    this.stacksetsScenarioStartMs = Date.now()
  }

  // Convenience getters that fold the active preset's inputs back to plain
  // @Input bindings for the template — avoids spreading complex objects in HTML.
  get s_migrationMode(): boolean {
    return this.stacksetsActivePreset.inputs.migrationMode ?? false
  }
  get s_sourceManagedByStackSet(): boolean {
    return this.stacksetsActivePreset.inputs.sourceManagedByStackSet ?? false
  }
  get s_targetManagedByStackSet(): boolean {
    return this.stacksetsActivePreset.inputs.targetManagedByStackSet ?? false
  }
  get s_sourceStackName(): string {
    return this.stacksetsActivePreset.inputs.sourceStackName ?? ''
  }
  get s_targetStackName(): string {
    return this.stacksetsActivePreset.inputs.targetStackName ?? ''
  }
  // Recheck overrides — flipped true briefly when the user clicks RECHECK on the CF-conflict
  // banner so the existing test-access spinner animation kicks in and the click is observable.
  private recheckSourceOverride = false
  private recheckTargetOverride = false
  get s_testingSource(): boolean {
    return this.recheckSourceOverride || (this.stacksetsActivePreset.inputs.testingSource ?? false)
  }
  get s_testingTarget(): boolean {
    return this.recheckTargetOverride || (this.stacksetsActivePreset.inputs.testingTarget ?? false)
  }

  onStacksetsTestAccess(scope: 'source' | 'target'): void {
    if (scope === 'source') {
      this.recheckSourceOverride = true
      setTimeout(() => (this.recheckSourceOverride = false), 1200)
    } else {
      this.recheckTargetOverride = true
      setTimeout(() => (this.recheckTargetOverride = false), 1200)
    }
  }
  get s_sourceError(): boolean {
    return this.stacksetsActivePreset.inputs.sourceError ?? false
  }
  get s_targetError(): boolean {
    return this.stacksetsActivePreset.inputs.targetError ?? false
  }
  get s_sourceTestCount(): number {
    return this.stacksetsActivePreset.inputs.sourceTestCount ?? 0
  }
  get s_targetTestCount(): number {
    return this.stacksetsActivePreset.inputs.targetTestCount ?? 0
  }
  get s_sourceAccessState(): AccessState {
    return this.stacksetsActivePreset.inputs.sourceAccessState ?? AccessState.UPDATE_TEMPLATE
  }
  get s_targetAccessState(): AccessState {
    return this.stacksetsActivePreset.inputs.targetAccessState ?? AccessState.UPDATE_TEMPLATE
  }

  // ViewChild refs for the modals section. Template-based modals (those that
  // accept a [dialogTemplate]) are opened via the ModalDialogComponent ref;
  // self-wrapped modals (the *ModalComponent classes) expose their own open()
  // method that drives an inner modal-dialog.
  @ViewChild('launchRecoveryDialog') launchRecoveryDialog: ModalDialogComponent
  @ViewChild('launchTestDialog') launchTestDialog: ModalDialogComponent
  // Drives the [launching] input on the launch-test-dialog mock so the
  // "Preparing to launch your test..." snapshot view can be exercised on
  // the test page. Click TEST RECOVERY to flip; click Open Launch Test
  // again to reset back to the form view.
  public mockLaunchTestLaunching: boolean = false
  @ViewChild('concludeRecoveryDialog') concludeRecoveryDialog: ModalDialogComponent
  @ViewChild('concludeTestDialog') concludeTestDialog: ModalDialogComponent
  @ViewChild('stopTestDialog') stopTestDialog: ModalDialogComponent
  @ViewChild('failbackDialog') failbackDialog: ModalDialogComponent
  @ViewChild('unquarantineDialog') unquarantineDialog: ModalDialogComponent
  @ViewChild('confirmActionDialog') confirmActionDialog: ModalDialogComponent
  @ViewChild('appTestConfigDialog') appTestConfigDialog: ModalDialogComponent
  @ViewChild('appTestConfigAdvancedDialog') appTestConfigAdvancedDialog: ModalDialogComponent
  @ViewChild('updateAccessDialog') updateAccessDialog: ModalDialogComponent
  @ViewChild('editIntegrationDialog') editIntegrationDialog: ModalDialogComponent
  @ViewChild('areYouSureDialog') areYouSureDialog: ModalDialogComponent
  @ViewChild('submitTicketDialog') submitTicketDialog: ModalDialogComponent
  @ViewChild('confirmTicketDialog') confirmTicketDialog: ModalDialogComponent
  @ViewChild('releaseRpsDialog') releaseRpsDialog: ModalDialogComponent
  @ViewChild('confirmManualReplicationDialog') confirmManualReplicationDialog: ModalDialogComponent
  @ViewChild('confirmEbsSnapshotDialog') confirmEbsSnapshotDialog: ModalDialogComponent
  @ViewChild('awsEventLogsDialog') awsEventLogsDialog: ModalDialogComponent
  @ViewChild('newRoleModal') newRoleModal: NewRoleModalComponent
  @ViewChild('editRoleModal') editRoleModal: EditRoleModalComponent
  @ViewChild('newGroupModal') newGroupModal: NewGroupModalComponent
  @ViewChild('editGroupModal') editGroupModal: EditGroupModalComponent
  @ViewChild('editUserModal') editUserModal: EditUserModalComponent
  @ViewChild('inviteUsersModal') inviteUsersModal: InviteUsersModalComponent
  @ViewChild('newApiKeyModal') newApiKeyModal: NewApiKeyModalComponent
  @ViewChild('editApiKeyModal') editApiKeyModal: EditApiKeyModalComponent
  @ViewChild('idpModal') idpModal: IdpModalComponent

  // Mock data for modals that need a typed input. Cast as any — these are
  // dev-only fixtures sized to render the modal, not exercise its logic.
  readonly mockRole: any = { roleId: 'mock-role', name: 'Mock Role', description: 'A test role', policies: [] }
  readonly mockGroup: any = {
    groupId: 'mock-group',
    name: 'Mock Group',
    description: 'A test group',
    users: [],
    roles: [],
  }
  readonly mockUser: any = {
    userId: 'mock-user',
    email: 'tester@arpio.io',
    firstName: 'Test',
    lastName: 'User',
    isSamlUser: false,
    groups: [],
  }
  readonly mockApiKey: any = { apiKeyId: 'mock-key', name: 'Mock API key', accessLevel: 'admin' }
  // Field names must match IdpModalData: { type, operation, existingIdp? }.
  // Default to msEntra/edit so the dialog renders the Name + Directory ID
  // inputs and not just the title.
  readonly mockIdpData: any = {
    type: 'msEntra',
    operation: 'edit',
    existingIdp: { name: 'Mock IdP', tenantId: 'mock-tenant-id' },
  }
  readonly mockIntegration: any = null

  // Stand-in HubSpot inline-message HTML for the support "ticket opened"
  // confirmation dialog so it renders without making a real HubSpot call.
  readonly mockHubspotResponse: string =
    '<p>Thanks! Your ticket has been opened. We typically reply within 4 business hours.</p>'

  readonly applicationTypeAws = ApplicationType.AWS

  // Mocks for the "Let's get you updated" (update-access) dialog so it renders
  // populated stack rows instead of empty skeleton placeholders.
  readonly mockAccessCheckResults = buildMockSyncPairAccess()
  readonly mockSourceEndpointUri = 'aws:111111111111:us-east-1'
  readonly mockTargetEndpointUri = 'aws:222222222222:us-west-2'

  get visibleAccounts(): Array<AccountDto> {
    if (!this.accounts) return []
    return this.showAllAccounts ? this.accounts : this.accounts.slice(0, this.ACCOUNTS_PREVIEW)
  }

  get hasMoreAccounts(): boolean {
    return this.accounts?.length > this.ACCOUNTS_PREVIEW
  }

  toggleSection(section: string): void {
    this.expandedSections[section] = !this.expandedSections[section]
  }

  // Mock applications keyed by sync phase so each modal opens with the right
  // state (STANDBY for launch flows, FAILOVER for conclude/unquarantine, etc).
  // Seeded in ngOnInit when no real apps load for this account.
  private mockStandbyApp: Application | null = null
  private mockFailoverApp: Application | null = null
  private mockFailoverTestApp: Application | null = null
  // A FAILOVER_TEST app whose restore has completed, in its own region pair so
  // it doesn't co-mingle with the still-restoring mockFailoverTestApp used by
  // "Cancel test (early)". Drives the full conclude-test flow (test outcome +
  // generate report); without a completed restore the dialog goes all-cancel
  // and hides those steps.
  private mockFailoverTestCompletedApp: Application | null = null
  // Azure counterparts for the quarantine paths (ARP-7636): launch-recovery now
  // renders its quarantine step for Azure (with Azure VM copy, malware-scan
  // sub-toggle hidden), and unquarantine now releases Azure VMs. The AWS mocks
  // above can't preview either, so give each an Azure STANDBY / FAILOVER app.
  private mockAzureStandbyApp: Application | null = null
  private mockAzureFailoverApp: Application | null = null
  // Sandbox-settings dialog: a separate FAILOVER_TEST app with the advanced
  // sandbox flag flipped on, so the test page can preview both basic
  // (CIDR + S3/DynamoDB) and advanced (domains + suggested + CIDR + suggested CIDRs).
  private mockSandboxAdvancedApp: Application | null = null

  constructor(
    private uiService: UiService,
    private apiService: ApiService,
    private router: Router,
    private applicationProviderService: ApplicationProviderService,
    private accountService: AccountService,
    private accessLevelService: AccessLevelService,
    private formBuilder: FormBuilder,
    private readonly regenerationPollService: StacksetsRegenerationPollService
  ) {}

  // Stub form for the inlined "Open a ticket" support dialog. Mirrors the
  // shape of SupportComponent.createTicketFormModel so the template renders;
  // submit is a no-op on the test page.
  readonly mockTicketFormModel: UntypedFormGroup = this.formBuilder.group({
    email: ['tester@arpio.io'],
    ticketPriority: ['LOW'],
    ticketSubject: ['Sample ticket subject'],
    ticketContent: ['Sample ticket description.'],
  })

  // Originals of the singletons applyMockSetup() patches; restored on
  // destroy so the patches don't leak into other routes.
  private patchedOriginals: {
    entitledForCapability?: typeof AccountService.prototype.entitledForCapability
    getUserPermissions?: typeof AccessLevelService.prototype.getUserPermissions
    getAccessTemplates?: typeof ApiService.prototype.getAccessTemplates
    testSyncPairAccess?: typeof ApiService.prototype.testSyncPairAccess
    fetchFirewallLogs?: typeof ApiService.prototype.fetchFirewallLogs
    getJob?: typeof ApiService.prototype.getJob
    getNetworkSandboxBlockedIps?: typeof ApiService.prototype.getNetworkSandboxBlockedIps
    getNetworkSandboxBlockedDomains?: typeof ApiService.prototype.getNetworkSandboxBlockedDomains
    getSyncPairResourceConfigs?: typeof ApiService.prototype.getSyncPairResourceConfigs
    setSyncPairResourceConfigs?: typeof ApiService.prototype.setSyncPairResourceConfigs
  } = {}

  // Toggled by the sandbox-events open methods so the patched fetchFirewallLogs
  // returns either an empty list (default empty/error state) or a populated list
  // (so reviewers can see the rendered table without a real backend).
  private mockFirewallEventsPopulated = false

  // Capabilities the patched entitledForCapability() reports as OFF. Empty by
  // default (everything on); network-sandbox scenarios set it to preview the
  // capability-gated states, and openWithApp() clears it for every other modal.
  private mockDisabledCapabilities = new Set<string>()

  // Network-sandbox scenario picker state + the apps built for each scenario.
  // Keyed by scenario key; seeded into the app cache alongside the base mocks.
  readonly launchTestSandboxScenarios = LAUNCH_TEST_SANDBOX_SCENARIOS
  readonly sandboxConfigScenarios = SANDBOX_CONFIG_SCENARIOS
  launchTestSandboxKey: string = LAUNCH_TEST_SANDBOX_SCENARIOS[0].key
  sandboxConfigKey: string = SANDBOX_CONFIG_SCENARIOS[0].key
  private sandboxScenarioApps: Application[] = []
  private launchTestSandboxAppMap = new Map<string, Application>()
  private sandboxConfigAppMap = new Map<string, Application>()

  // Group a sandbox scenario list by its `group` field (AWS / Azure),
  // preserving source order, for the picker <optgroup>s.
  private groupSandboxScenarios(list: readonly SandboxScenario[]): { group: string; scenarios: SandboxScenario[] }[] {
    const groups: { group: string; scenarios: SandboxScenario[] }[] = []
    for (const scenario of list) {
      const existing = groups.find((g) => g.group === scenario.group)
      if (existing) existing.scenarios.push(scenario)
      else groups.push({ group: scenario.group, scenarios: [scenario] })
    }
    return groups
  }
  get launchTestSandboxGroups() {
    return this.groupSandboxScenarios(this.launchTestSandboxScenarios)
  }
  get sandboxConfigGroups() {
    return this.groupSandboxScenarios(this.sandboxConfigScenarios)
  }

  // Cancels in-flight gate / data subscriptions when the component is
  // destroyed — without this, a late HTTP response could call
  // applyMockSetup() on a destroyed instance and leave singletons patched
  // with no future ngOnDestroy to restore them.
  private destroy$ = new Subject<void>()

  ngOnInit(): void {
    // Mock setup only runs in the allowed branch — customers who somehow
    // hit /test_page never get singletons monkey-patched.
    this.gateAndAccounts$()
      .pipe(takeUntil(this.destroy$))
      .subscribe(({ allowed, accounts }) => {
        if (!allowed) {
          this.gateState = 'denied'
          return
        }
        this.gateState = 'allowed'
        this.accounts = accounts
        this.applyMockSetup()
        if (accounts.length > 0) {
          this.loadAccountData(accounts[0].accountId)
        }
      })
  }

  ngOnDestroy(): void {
    this.destroy$.next()
    this.destroy$.complete()
    // Restore patched singletons so navigating away returns the SPA to
    // real entitlement / permission / API behavior. No-op if applyMockSetup
    // never ran (denied path).
    if (this.patchedOriginals.entitledForCapability) {
      this.accountService.entitledForCapability = this.patchedOriginals.entitledForCapability
    }
    if (this.patchedOriginals.getUserPermissions) {
      this.accessLevelService.getUserPermissions = this.patchedOriginals.getUserPermissions
    }
    if (this.patchedOriginals.getAccessTemplates) {
      this.apiService.getAccessTemplates = this.patchedOriginals.getAccessTemplates
    }
    if (this.patchedOriginals.testSyncPairAccess) {
      this.apiService.testSyncPairAccess = this.patchedOriginals.testSyncPairAccess
    }
    if (this.patchedOriginals.fetchFirewallLogs) {
      this.apiService.fetchFirewallLogs = this.patchedOriginals.fetchFirewallLogs
    }
    if (this.patchedOriginals.getJob) {
      this.apiService.getJob = this.patchedOriginals.getJob
    }
    if (this.patchedOriginals.getNetworkSandboxBlockedIps) {
      this.apiService.getNetworkSandboxBlockedIps = this.patchedOriginals.getNetworkSandboxBlockedIps
    }
    if (this.patchedOriginals.getNetworkSandboxBlockedDomains) {
      this.apiService.getNetworkSandboxBlockedDomains = this.patchedOriginals.getNetworkSandboxBlockedDomains
    }
    if (this.patchedOriginals.getSyncPairResourceConfigs) {
      this.apiService.getSyncPairResourceConfigs = this.patchedOriginals.getSyncPairResourceConfigs
    }
    if (this.patchedOriginals.setSyncPairResourceConfigs) {
      this.apiService.setSyncPairResourceConfigs = this.patchedOriginals.setSyncPairResourceConfigs
    }
    // Restore real regen poll timeout so navigating away leaves the singleton untouched.
    this.regenerationPollService.resetTimeoutMsForTesting()
    // Flush seeded mocks; the next account view refetches from the API.
    this.seedAppCache([])
    // Detach the screen-preview click blockers from both Recovery and Backup
    // displays. Capture-phase listeners outlive Angular's view destruction
    // unless explicitly removed.
    this._syncBlockerOnElement('recovery', null)
    this._syncBlockerOnElement('backup', null)
  }

  /**
   * Allow only legacy admins. `principal.adminRole != null` is what
   * access-level.service uses to derive `isLegacyAdmin`. listAccounts is
   * fetched in parallel so the accounts section populates without a
   * second round-trip. Any failure or 10s hang resolves to denied (404).
   */
  private gateAndAccounts$(): Observable<{ allowed: boolean; accounts: AccountDto[] }> {
    return forkJoin({
      principal: this.apiService.getAuthenticatedPrincipal(),
      rawAccounts: this.apiService.listAccounts(),
    }).pipe(
      map(({ principal, rawAccounts }) => ({
        allowed: principal.adminRole != null,
        accounts: rawAccounts ?? [],
      })),
      timeout(10000),
      catchError((err) => {
        console.warn('Test page: gate failed —', err)
        return of({ allowed: false, accounts: [] as AccountDto[] })
      })
    )
  }

  /**
   * Mock data + singleton patches that make the modal/issues showcase work
   * without a real backend. Only run after the gate has allowed access.
   * Captures originals first so ngOnDestroy can restore them.
   */
  private applyMockSetup(): void {
    this.statusResources = buildStatusResources()
    this.parentChildResources = buildParentChildScenarioResources()
    this.issues = buildAllMockIssues()
    this.duplicateRowsIssues = buildDuplicateRowsScenario()
    this.tripledNameIssues = buildTripledNameScenario()

    // Capability flags default ON for the modals showcase so dialogs render
    // every conditional step (quarantine, sandbox, advanced sandbox, RBAC,
    // app-group restore, etc.) regardless of the dev account's real
    // entitlements. Network-sandbox scenarios populate mockDisabledCapabilities
    // to switch specific capabilities OFF and preview the capability-gated
    // states; the generic openWithApp() clears it so every other button is
    // still all-on. Original captured for ngOnDestroy restore.
    this.patchedOriginals.entitledForCapability = this.accountService.entitledForCapability.bind(this.accountService)
    ;(this.accountService as any).entitledForCapability = (capability: string) =>
      !this.mockDisabledCapabilities.has(capability)

    // Treat the test-page user as a legacy admin so RBAC permission filtering
    // doesn't drop our mock applications out of the dialog candidate lists.
    this.patchedOriginals.getUserPermissions = this.accessLevelService.getUserPermissions.bind(this.accessLevelService)
    ;(this.accessLevelService as any).getUserPermissions = () =>
      of({ isLegacyAdmin: true, appPermissionsMap: new Map(), accountPermissions: new Set() } as any)

    // Stub the API calls update-access-dialog makes so it renders with
    // populated content instead of skeleton rows.
    this.patchedOriginals.getAccessTemplates = this.apiService.getAccessTemplates.bind(this.apiService)
    ;(this.apiService as any).getAccessTemplates = () => of(buildMockAccessTemplates())
    this.patchedOriginals.testSyncPairAccess = this.apiService.testSyncPairAccess.bind(this.apiService)
    ;(this.apiService as any).testSyncPairAccess = () => of(buildMockSyncPairAccess())

    // Sandbox event log: return an empty or populated mock response based on
    // which test-page button opened the dialog. The real API rejects mock IDs
    // so without this stub the dialog would always render the "failed to load"
    // state and the populated view would be unreachable.
    this.patchedOriginals.fetchFirewallLogs = this.apiService.fetchFirewallLogs.bind(this.apiService)
    ;(this.apiService as any).fetchFirewallLogs = () =>
      of({
        events: this.mockFirewallEventsPopulated ? buildMockFirewallEvents() : [],
        awsConsoleUrl: 'https://console.aws.amazon.com/cloudwatch/',
      })

    // application-test-config-dialog fetches recently-blocked IPs/domains to
    // populate its "Suggested" lists when advanced sandbox is on. The real
    // endpoints reject the mock app IDs, so stub them with sample results so
    // the advanced scenarios render populated suggestions instead of erroring.
    this.patchedOriginals.getNetworkSandboxBlockedIps = this.apiService.getNetworkSandboxBlockedIps.bind(
      this.apiService
    )
    ;(this.apiService as any).getNetworkSandboxBlockedIps = () =>
      of({
        status: 'ok',
        results: [
          { endpoint: '203.0.113.24', count: 12 },
          { endpoint: '198.51.100.7', count: 5 },
        ],
      })
    this.patchedOriginals.getNetworkSandboxBlockedDomains = this.apiService.getNetworkSandboxBlockedDomains.bind(
      this.apiService
    )
    ;(this.apiService as any).getNetworkSandboxBlockedDomains = () =>
      of({
        status: 'ok',
        results: [
          { endpoint: 'telemetry.example.com', count: 9 },
          { endpoint: 'updates.vendor.io', count: 3 },
        ],
      })

    // unquarantine-dialog loads existing resource configs on open and writes
    // them back on UNQUARANTINE. The real endpoints reject the mock IDs, so
    // stub the read with no configs (the dialog builds defaults for anything
    // missing) and echo the write back so the optimistic status update runs.
    this.patchedOriginals.getSyncPairResourceConfigs = this.apiService.getSyncPairResourceConfigs.bind(this.apiService)
    ;(this.apiService as any).getSyncPairResourceConfigs = () => of([])
    this.patchedOriginals.setSyncPairResourceConfigs = this.apiService.setSyncPairResourceConfigs.bind(this.apiService)
    ;(this.apiService as any).setSyncPairResourceConfigs = (_accountId: string, _syncPair: unknown, configs: unknown) =>
      of(configs)

    // StackSets regen-job poll: drive the gating in stacksets-instructions and
    // stacksets-update-instructions through every status sequence the production
    // components handle. Reads stacksetsJobScenario + stacksetsScenarioStartMs
    // so each tab click anchors timed transitions to the user's click.
    this.patchedOriginals.getJob = this.apiService.getJob.bind(this.apiService)
    ;(this.apiService as any).getJob = (accountId: string, jobId: string): Observable<JobDto> => {
      const mk = (status: JobStatus) => buildMockJobDto(jobId, status, accountId)
      const elapsed = Date.now() - this.stacksetsScenarioStartMs
      switch (this.stacksetsJobScenario) {
        case 'instant_done':
          return of(mk('done'))
        case 'skipped':
          return of(mk('skipped'))
        case 'running_then_done':
          return of(elapsed < 10_000 ? mk('running') : mk('done'))
        case 'running_then_failed':
          // Return failed on the first poll — running_timeout already covers the long-running case.
          return of(mk('failed'))
        case 'running_timeout':
          return of(mk('running'))
        case 'http_error':
          return throwError(
            () => new HttpError(500, 'mock failure', '/api/accounts/test/jobs/' + jobId, { message: 'Mock 500' })
          )
        case 'none':
        default:
          return of(mk('done'))
      }
    }

    // Seed mock apps so dialogs render even if the dev account is empty.
    this.mockStandbyApp = buildMockApplication({
      applicationId: 'mock-standby',
      name: 'Mock standby app',
      syncPhase: SyncPhaseDto.STANDBY,
    })
    this.mockFailoverApp = buildMockApplication({
      applicationId: 'mock-failover',
      name: 'Mock failover app',
      syncPhase: SyncPhaseDto.FAILOVER,
      // Quarantined resources so the unquarantine dialog renders a populated
      // list (with clean + unclean rows) instead of an empty state.
      restoreResources: buildMockQuarantinedResources(),
    })
    this.mockFailoverTestApp = buildMockApplication({
      applicationId: 'mock-failover-test',
      name: 'Mock failover-test app',
      syncPhase: SyncPhaseDto.FAILOVER_TEST,
    })
    const restoreCompletedAt = new Date(Date.now() - 30 * 60 * 1000)
    this.mockFailoverTestCompletedApp = buildMockApplication({
      applicationId: 'mock-failover-test-completed',
      name: 'Mock failover-test app (restore complete)',
      syncPhase: SyncPhaseDto.FAILOVER_TEST,
      // Own region pair keeps it out of the "Cancel test (early)" candidate list.
      sourceRegion: 'us-east-2',
      targetRegion: 'us-west-1',
      lastRestoreComplete: restoreCompletedAt,
      lastRestoreSuccess: restoreCompletedAt,
    })
    this.mockSandboxAdvancedApp = buildMockApplication({
      applicationId: 'mock-sandbox-advanced',
      name: 'Mock sandbox-advanced app',
      syncPhase: SyncPhaseDto.FAILOVER_TEST,
      advancedNetworkSandbox: true,
    })
    this.mockAzureStandbyApp = buildMockApplication({
      applicationId: 'mock-azure-standby',
      name: 'Mock Azure standby app',
      type: ApplicationType.AZURE,
      syncPhase: SyncPhaseDto.STANDBY,
    })
    this.mockAzureFailoverApp = buildMockApplication({
      applicationId: 'mock-azure-failover',
      name: 'Mock Azure failover app',
      type: ApplicationType.AZURE,
      syncPhase: SyncPhaseDto.FAILOVER,
      // Azure VM (+ one scale set) quarantined resources so the unquarantine
      // dialog renders the Azure path populated instead of empty.
      restoreResources: buildMockAzureQuarantinedResources(),
    })
    this.siblingStandbyApps = [
      buildMockApplication({
        applicationId: 'mock-standby-2',
        name: 'Mock checkout-service',
        syncPhase: SyncPhaseDto.STANDBY,
      }),
      buildMockApplication({
        applicationId: 'mock-standby-3',
        name: 'Mock orders-api',
        syncPhase: SyncPhaseDto.STANDBY,
      }),
    ]
    this.siblingFailoverApps = [
      buildMockApplication({
        applicationId: 'mock-failover-2',
        name: 'Mock auth-service (failed over)',
        syncPhase: SyncPhaseDto.FAILOVER,
      }),
    ]
    this.siblingFailoverTestApps = [
      buildMockApplication({
        applicationId: 'mock-failover-test-2',
        name: 'Mock billing-service (in test)',
        syncPhase: SyncPhaseDto.FAILOVER_TEST,
      }),
    ]
    this.buildSandboxScenarioApps()
    this.seedAppCache([
      this.mockStandbyApp,
      ...this.siblingStandbyApps,
      this.mockFailoverApp,
      ...this.siblingFailoverApps,
      this.mockFailoverTestApp,
      this.mockFailoverTestCompletedApp,
      ...this.siblingFailoverTestApps,
      this.mockSandboxAdvancedApp,
      this.mockAzureStandbyApp,
      this.mockAzureFailoverApp,
      ...this.sandboxScenarioApps,
    ])
    this.application = this.mockStandbyApp
  }

  // Build one anchor app per network-sandbox scenario (ARP-7142), each in its
  // own region pair so the launch dialogs' region-scoped candidate lists stay
  // isolated. launch-test anchors are STANDBY; sandbox-settings anchors are
  // FAILOVER_TEST. Scenarios with siblingFailoverTest also seed that many
  // in-test apps in the same region to drive sandboxFrozen / the multi-app
  // "also applies to" notice.
  private buildSandboxScenarioApps(): void {
    this.sandboxScenarioApps = []
    this.launchTestSandboxAppMap.clear()
    this.sandboxConfigAppMap.clear()

    const seedScenario = (
      scenario: SandboxScenario,
      prefix: string,
      index: number,
      anchorPhase: SyncPhaseDto,
      appMap: Map<string, Application>
    ) => {
      const sourceRegion = `${prefix}-src-${index}`
      const targetRegion = `${prefix}-tgt-${index}`
      const anchor = buildMockApplication({
        ...scenario.appOpts,
        applicationId: `${prefix}-${scenario.key}`,
        name: scenario.label,
        syncPhase: anchorPhase,
        sourceRegion,
        targetRegion,
      })
      appMap.set(scenario.key, anchor)
      this.sandboxScenarioApps.push(anchor)
      for (let i = 0; i < (scenario.siblingFailoverTest ?? 0); i++) {
        this.sandboxScenarioApps.push(
          buildMockApplication({
            ...scenario.appOpts,
            applicationId: `${prefix}-${scenario.key}-sibling-${i}`,
            name: `${scenario.label} — sibling ${i + 1}`,
            syncPhase: SyncPhaseDto.FAILOVER_TEST,
            sourceRegion,
            targetRegion,
          })
        )
      }
    }

    this.launchTestSandboxScenarios.forEach((scenario, index) =>
      seedScenario(scenario, 'sb-lt', index, SyncPhaseDto.STANDBY, this.launchTestSandboxAppMap)
    )
    this.sandboxConfigScenarios.forEach((scenario, index) =>
      seedScenario(scenario, 'sb-tc', index, SyncPhaseDto.FAILOVER_TEST, this.sandboxConfigAppMap)
    )
  }

  // Sibling mocks per phase — needed so dialog candidate lists are populated.
  // On the instance so the post-loadAllApplications re-seed can splice them back.
  private siblingStandbyApps: Application[] = []
  private siblingFailoverApps: Application[] = []
  private siblingFailoverTestApps: Application[] = []

  /** Load real syncPair + apps; errors are non-fatal (mocks remain). */
  private loadAccountData(accountId: string): void {
    this.apiService
      .listSyncPairs(accountId)
      .pipe(takeUntil(this.destroy$))
      .subscribe(
        (syncPairs) => {
          if (Array.isArray(syncPairs) && syncPairs.length > 0 && syncPairs[0] && syncPairs[0].sourceAwsAccountId) {
            this.syncPair = new SyncPair(this.apiService, accountId, syncPairs[0])
          } else {
            console.warn('No valid sync pairs found:', syncPairs)
            this.syncPair = new SyncPair(this.apiService, accountId, fakeSyncPair)
          }

          // loadAllApplications$ wipes the cache synchronously on call —
          // re-seed mocks immediately so dialogs opened mid-load still find them.
          const realLoad$ = this.applicationProviderService.loadAllApplications$(accountId)
          this.seedAppCache([
            this.mockStandbyApp,
            ...this.siblingStandbyApps,
            this.mockFailoverApp,
            ...this.siblingFailoverApps,
            this.mockFailoverTestApp,
            this.mockFailoverTestCompletedApp,
            ...this.siblingFailoverTestApps,
            this.mockSandboxAdvancedApp,
            this.mockAzureStandbyApp,
            this.mockAzureFailoverApp,
            ...this.sandboxScenarioApps,
          ])
          realLoad$.pipe(takeUntil(this.destroy$)).subscribe(
            () => {
              const apps = this.applicationProviderService.loadedApplications()
              const realApp = apps.find(
                (a) =>
                  a.applicationId !== this.mockStandbyApp?.applicationId &&
                  a.applicationId !== this.mockFailoverApp?.applicationId &&
                  a.applicationId !== this.mockFailoverTestApp?.applicationId
              )
              if (realApp) {
                this.application = realApp
              }
              if (!apps.some((a) => a.applicationId === this.mockStandbyApp?.applicationId)) {
                this.seedAppCache([
                  this.mockStandbyApp!,
                  ...this.siblingStandbyApps,
                  this.mockFailoverApp!,
                  ...this.siblingFailoverApps,
                  this.mockFailoverTestApp!,
                  this.mockFailoverTestCompletedApp!,
                  ...this.siblingFailoverTestApps,
                  this.mockSandboxAdvancedApp!,
                  this.mockAzureStandbyApp!,
                  this.mockAzureFailoverApp!,
                  ...this.sandboxScenarioApps,
                  ...apps,
                ])
              }
            },
            (err) => {
              console.warn('Failed to load applications:', err)
            }
          )
        },
        (err) => {
          console.warn('Test page: failed to load syncPairs —', err)
        }
      )
  }

  scrollTo(id: string, sectionKey: string): void {
    // Expand the section first, then scroll after Angular renders the content
    this.expandedSections[sectionKey] = true
    setTimeout(() => {
      document.getElementById(id)?.scrollIntoView({ behavior: 'smooth', block: 'start' })
    })
  }

  doLogout() {
    // Swallow errors — handleArpioError would silently redirect a stale
    // session into the login flow, which we don't want here.
    this.apiService.logout().subscribe(
      () => console.log('Logged out.'),
      (err) => console.warn('Test page: logout failed —', err)
    )
  }

  // Reach into the singleton's private cache the same way we patch the
  // other shared singletons above — keeps all test-only seeding in this file
  // so production services don't carry a *ForTesting surface.
  private seedAppCache(apps: Application[]): void {
    ;(this.applicationProviderService as any).applicationCache = new Map(apps.map((a) => [a.applicationId, a]))
  }

  // -------- Modal open helpers (visual testing only) --------

  // Swap `application` to the variant matching the modal's expected sync phase
  // before the modal-dialog template re-evaluates. Wait one tick so Angular
  // mounts the dialog with the right input value.
  private openWithApp(app: Application | null, dialog: ModalDialogComponent | undefined) {
    // Every non-sandbox-scenario modal expects all capabilities on. The
    // sandbox openers below set mockDisabledCapabilities and bypass this
    // helper, so clearing it here can't stomp their state.
    this.mockDisabledCapabilities.clear()
    if (app) this.application = app
    setTimeout(() => dialog?.open(), 0)
  }

  // Network-sandbox scenario openers (ARP-7142). Apply the scenario's disabled
  // capabilities, swap to its pre-built anchor app, then open the dialog. Kept
  // separate from openWithApp() so the capability set survives to ngOnInit.
  private openSandboxScenario(
    app: Application | undefined,
    disabledCapabilities: string[] | undefined,
    dialog: ModalDialogComponent | undefined
  ) {
    this.mockDisabledCapabilities = new Set(disabledCapabilities ?? [])
    this.mockLaunchTestLaunching = false
    if (app) this.application = app
    setTimeout(() => dialog?.open(), 0)
  }

  openLaunchTestSandboxScenario() {
    const scenario = this.launchTestSandboxScenarios.find((s) => s.key === this.launchTestSandboxKey)
    if (!scenario) return
    this.openSandboxScenario(
      this.launchTestSandboxAppMap.get(scenario.key),
      scenario.disabledCapabilities,
      this.launchTestDialog
    )
  }

  openSandboxConfigScenario() {
    const scenario = this.sandboxConfigScenarios.find((s) => s.key === this.sandboxConfigKey)
    if (!scenario) return
    this.openSandboxScenario(
      this.sandboxConfigAppMap.get(scenario.key),
      scenario.disabledCapabilities,
      this.appTestConfigDialog
    )
  }

  // Wired to the sandbox dialogs' close/cancel so a capability-off scenario
  // doesn't leave capabilities disabled for the always-rendered background
  // previews after the modal closes.
  resetMockCapabilities() {
    this.mockDisabledCapabilities.clear()
  }

  openLaunchRecovery() {
    this.openWithApp(this.mockStandbyApp, this.launchRecoveryDialog)
  }
  openLaunchRecoveryAzure() {
    this.openWithApp(this.mockAzureStandbyApp, this.launchRecoveryDialog)
  }
  openLaunchTest() {
    this.mockLaunchTestLaunching = false
    this.openWithApp(this.mockStandbyApp, this.launchTestDialog)
  }
  openConcludeRecovery() {
    this.openWithApp(this.mockFailoverApp, this.concludeRecoveryDialog)
  }
  openConcludeTest() {
    // Use the restore-complete app so the full conclude flow (test outcome +
    // generate report) shows; the still-restoring app would go all-cancel.
    this.openWithApp(this.mockFailoverTestCompletedApp, this.concludeTestDialog)
  }
  openStopTest() {
    this.openWithApp(this.mockFailoverTestApp, this.stopTestDialog)
  }
  openFailback() {
    this.openWithApp(this.mockFailoverApp, this.failbackDialog)
  }
  openUnquarantine() {
    this.openWithApp(this.mockFailoverApp, this.unquarantineDialog)
  }
  openUnquarantineAzure() {
    this.openWithApp(this.mockAzureFailoverApp, this.unquarantineDialog)
  }
  openConfirmAction() {
    this.confirmActionDialog?.open()
  }
  openAppTestConfig() {
    this.openWithApp(this.mockFailoverTestApp, this.appTestConfigDialog)
  }
  openAppTestConfigAdvanced() {
    this.openWithApp(this.mockSandboxAdvancedApp, this.appTestConfigAdvancedDialog)
  }
  openUpdateAccess() {
    this.updateAccessDialog?.open()
  }
  openEditIntegration() {
    this.editIntegrationDialog?.open()
  }
  openSubmitTicket() {
    this.submitTicketDialog?.open()
  }
  openConfirmTicket() {
    this.confirmTicketDialog?.open()
  }
  openReleaseRps() {
    this.releaseRpsDialog?.open()
  }
  openConfirmManualReplication() {
    this.confirmManualReplicationDialog?.open()
  }
  openSandboxEvents() {
    // Real opener requires advancedNetworkSandboxEnabled + failoverTest phase,
    // so seed the mock with the matching variant before the dialog mounts.
    this.mockFirewallEventsPopulated = false
    this.openWithApp(this.mockSandboxAdvancedApp, this.awsEventLogsDialog)
  }
  openSandboxEventsPopulated() {
    this.mockFirewallEventsPopulated = true
    this.openWithApp(this.mockSandboxAdvancedApp, this.awsEventLogsDialog)
  }
  openConfirmEbsSnapshot() {
    this.confirmEbsSnapshotDialog?.open()
  }
  openAreYouSure() {
    this.openWithApp(this.mockStandbyApp, this.areYouSureDialog)
  }
  openNewRole() {
    this.newRoleModal?.open()
  }
  openEditRole() {
    this.editRoleModal?.open(this.mockRole)
  }
  openNewGroup() {
    this.newGroupModal?.open()
  }
  openEditGroup() {
    this.editGroupModal?.open(this.mockGroup)
  }
  openEditUser() {
    this.editUserModal?.open(this.mockUser)
  }
  openInviteUsers() {
    this.inviteUsersModal?.open()
  }
  openNewApiKey() {
    this.newApiKeyModal?.open()
  }
  openEditApiKey() {
    this.editApiKeyModal?.open(this.mockApiKey)
  }
  openIdpModal() {
    this.idpModal?.open(this.mockIdpData)
  }

  toastMenuOpen = false

  // Drives the test-page toast tester button (top-right). Ordered visual-first
  // (layout sanity) → handler-specific (real backend payloads). Verbiage in the
  // passthrough group is copied verbatim from arpio-api UnprocessableEntity
  // raises (restore_api.py + arpio_core/issues) so the preview matches prod.
  readonly toastGroups: ReadonlyArray<{
    label: string
    variants: ReadonlyArray<{ label: string; fire: () => void }>
  }> = [
    {
      label: 'Layout & wrapping',
      variants: [
        {
          label: 'Short message',
          fire: () =>
            this.uiService.announceError({
              message: 'Something went wrong',
              detailedMessage: 'Please try again.',
            }),
        },
        {
          label: 'Long single-line (width clamp)',
          fire: () =>
            this.uiService.announceError({
              message: 'Failed to update resource',
              detailedMessage:
                'A long detailedMessage with no explicit line breaks so we can verify the toast wraps cleanly inside the fixed-width container without bleeding past the right edge of the viewport.',
            }),
        },
        {
          label: 'Multi-line (preserves \\n)',
          fire: () =>
            this.uiService.announceError({
              message: 'Multi-step failure',
              detailedMessage: 'Step one failed.\nStep two failed.\n\nStep three was skipped.',
            }),
        },
      ],
    },
    {
      label: 'Plain API codes (no body)',
      variants: [
        {
          label: 'Connection error (0)',
          fire: () => this.uiService.announceApiError({ responseCode: 0 } as any),
        },
        {
          label: 'Invalid request (400)',
          fire: () => this.uiService.announceApiError({ responseCode: 400 } as any),
        },
        {
          label: 'Authentication required (401)',
          fire: () => this.uiService.announceApiError({ responseCode: 401 } as any),
        },
        {
          label: 'Access denied (403)',
          fire: () => this.uiService.announceApiError({ responseCode: 403 } as any),
        },
        {
          label: 'Not found (404)',
          fire: () => this.uiService.announceApiError({ responseCode: 404 } as any),
        },
        {
          label: 'Conflict (409)',
          fire: () => this.uiService.announceApiError({ responseCode: 409 } as any),
        },
        {
          label: 'Invalid input (422)',
          fire: () => this.uiService.announceApiError({ responseCode: 422 } as any),
        },
        {
          label: 'Server error (500)',
          fire: () => this.uiService.announceApiError({ responseCode: 500 } as any),
        },
        {
          label: 'Unrecognized code (999)',
          fire: () => this.uiService.announceApiError({ responseCode: 999 } as any),
        },
      ],
    },
    {
      label: 'Backend message passthrough (4xx with body)',
      variants: [
        {
          label: 'Multi-app launch failure (422)',
          fire: () =>
            this.uiService.announceApiError({
              responseCode: 422,
              body: {
                message:
                  'Application app-prod-web cannot be restored because of the following issues:\n' +
                  '  The applied recovery point cannot be changed because some applications are already in a test or failed over state.\n\n' +
                  'Application app-prod-api cannot be restored because of the following issues:\n' +
                  '  The applied recovery point cannot be changed because some applications are already in a test or failed over state.',
              },
            } as any),
        },
        {
          label: 'Single-app launch failure with issues list (422)',
          fire: () =>
            this.uiService.announceApiError({
              responseCode: 422,
              body: {
                message:
                  'Application app-prod-web cannot be restored because of the following issues:\n' +
                  '  The applied recovery point cannot be changed because some applications are already in a test or failed over state.',
              },
            } as any),
        },
        {
          label: 'Single-app launch failure, invalid request (422)',
          fire: () =>
            this.uiService.announceApiError({
              responseCode: 422,
              body: { message: 'Application app-prod-web cannot be restored because the request was invalid' },
            } as any),
        },
        {
          label: 'Restore disabled for app (422)',
          fire: () =>
            this.uiService.announceApiError({
              responseCode: 422,
              body: { message: 'Restore is disabled for application app-prod-web' },
            } as any),
        },
        {
          label: 'No applications to restore (422)',
          fire: () =>
            this.uiService.announceApiError({
              responseCode: 422,
              body: { message: 'No applications to restore.' },
            } as any),
        },
        {
          label: 'Account not entitled (422)',
          fire: () =>
            this.uiService.announceApiError({
              responseCode: 422,
              body: { message: 'This Arpio account is not entitled to restore applications.' },
            } as any),
        },
        {
          label: 'Retry only completed jobs (422)',
          fire: () =>
            this.uiService.announceApiError({
              responseCode: 422,
              body: { message: 'Only completed jobs can be retried. Job status: IN_PROGRESS' },
            } as any),
        },
        {
          label: 'Resume recovery unavailable (422)',
          fire: () =>
            this.uiService.announceApiError({
              responseCode: 422,
              body: {
                message:
                  'Resume recovery is not available for this application. Cause: RECOVERY_POINT_NO_LONGER_AVAILABLE',
              },
            } as any),
        },
        {
          label: 'RP lock acquisition failed (422)',
          fire: () =>
            this.uiService.announceApiError({
              responseCode: 422,
              body: {
                message:
                  'Restore request failed because of the following issue: A different restore is already in flight for this recovery point.',
              },
            } as any),
        },
        {
          label: 'Generic backend detail (400)',
          fire: () =>
            this.uiService.announceApiError({
              responseCode: 400,
              body: { message: 'Required field "applicationId" is missing from the request body.' },
            } as any),
        },
      ],
    },
    {
      label: 'Specialized substring-matched handlers',
      variants: [
        {
          label: 'Duplicate role name (422)',
          fire: () =>
            this.uiService.announceApiError({
              responseCode: 422,
              body: { message: 'Role name already exists.' },
            } as any),
        },
        {
          label: 'SAML metadata expired (422)',
          fire: () =>
            this.uiService.announceApiError({
              responseCode: 422,
              body: { message: 'SAML metadata has expired.' },
            } as any),
        },
        {
          label: 'SAML metadata missing EntityDescriptor (422)',
          fire: () =>
            this.uiService.announceApiError({
              responseCode: 422,
              body: { message: 'SAML metadata is missing the entityDescriptor element.' },
            } as any),
        },
        {
          label: 'Email verification required (403)',
          fire: () =>
            this.uiService.announceApiError({
              responseCode: 403,
              body: { message: 'You must verify your email address before adding users.' },
            } as any),
        },
        {
          label: 'Email cooldown 90s (403)',
          fire: () =>
            this.uiService.announceApiError({
              responseCode: 403,
              body: { message: 'You must wait at least 90 seconds since the last email was sent.' },
            } as any),
        },
      ],
    },
    {
      label: 'Real-world examples',
      variants: [
        {
          label: 'Restore failed, recovery point missing',
          fire: () =>
            this.uiService.announceError({
              message: 'Recovery Failed',
              detailedMessage:
                'The recovery point you selected is no longer available. It may have been deleted or pruned by your retention policy. Refresh and try a different recovery point.',
            }),
        },
        {
          label: 'HubSpot widget unavailable',
          fire: () =>
            this.uiService.announceError({
              message: 'Unable to find HubSpot chat widget.',
              detailedMessage: 'Unable to find HubSpot chat widget.',
            }),
        },
      ],
    },
  ]

  toggleToastMenu(event: MouseEvent): void {
    event.stopPropagation()
    event.preventDefault()
    this.toastMenuOpen = !this.toastMenuOpen
  }

  fireToast(event: MouseEvent, fire: () => void): void {
    event.stopPropagation()
    event.preventDefault()
    fire()
    this.toastMenuOpen = false
  }

  onPanelClick(event: MouseEvent): void {
    event.stopPropagation()
  }

  @HostListener('document:click')
  closeToastMenuOnDocClick(): void {
    if (this.toastMenuOpen) this.toastMenuOpen = false
  }

  @HostListener('document:keydown.escape')
  closeToastMenuOnEscape(): void {
    if (this.toastMenuOpen) this.toastMenuOpen = false
  }
}
